import { useMemo, useEffect, useState } from 'react';

/**
 * Reports when a watched element re-enters the viewport so headers can react to layout shifts. Observes the ref with an IntersectionObserver using a thousand-step threshold and a -2px root margin, incrementing a counter on each intersection, and clears the counter and disconnects the observer on cleanup.
 *
 * @param ref - Ref of the element to observe.
 * @returns Counter incremented on every intersection, or null after cleanup.
 */
const useElementChange = (ref: any) => {
  const [intersectionObserverEntry, setIntersectionObserverEntry] = useState<number | null>(0);
  /**
   * Computes a memoized array of 1000 intersection threshold values stepped by 0.001. Derived from a fixed-length array mapped to fractional visibility ratios for the IntersectionObserver.
   *
   * @returns The list of threshold ratios from 0 to 0.999.
   */
  const threshold = useMemo(() => [...Array(1000)].map((_, index) => index * 0.001), []);

  useEffect(() => {
    const option: any = {
      root: null,
      rootMargin: '-2px',
      threshold,
    };
    if (ref.current && typeof IntersectionObserver === 'function') {
      /**
       * Reacts to IntersectionObserver notifications for the watched element. Increments the intersection counter whenever the first entry reports the element as intersecting.
       *
       * @param entries - Entries reported by the observer.
       * @returns Nothing.
       */
      const handler = (entries: IntersectionObserverEntry[]) => {
        if (entries[0]?.isIntersecting) {
          setIntersectionObserverEntry((prev) => (prev ?? 0) + 1);
        }
      };

      const observer = new IntersectionObserver(handler, option);
      observer.observe(ref.current);

      return () => {
        setIntersectionObserverEntry(null);
        observer.disconnect();
      };
    }
    return () => {};
  }, [ref.current]);

  return intersectionObserverEntry;
};

export default useElementChange;
